import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { chevronBackOutline } from 'ionicons/icons'
import type React from 'react'
import type { PropsWithChildren } from 'react'
import { useHistory } from 'react-router-dom'
import { useShortLinkContext } from '../context/ShortLinkContext'
import { useTabNavigation } from '../context/TabNavigationContext'
import { useSwipeBack } from '../hooks/useSwipeBack'

export type StandardLayoutProps = PropsWithChildren<{
  title?: React.ReactNode
  id?: string
  hideHeader?: boolean
  headerEndButtons?: React.ReactNode
  collapsingTitle?: boolean
  defaultBackTo?: string
}>

export const StandardLayout = (props: StandardLayoutProps) => {
  const history = useHistory()
  const shortLink = useShortLinkContext()
  const tabNavigation = useTabNavigation()

  // Plain client-side back. IonBackButton's defaultHref fallback was doing a
  // HARD document navigation in this (keep-alive) outlet setup — that reloaded
  // the whole page on Back (the white flash). Inside the tab shell Back stays
  // in the tab (pop, or the parent route after a tab switch); the mini-app has
  // no tab shell, so it pops when it can and falls back to the parent route
  // (e.g. opened via a deep link).
  const handleBack = () => {
    // On the mini-app's entry screen a history pop would leave the site
    // entirely (whatever page the SMS tap replaced), so Back exits to the main
    // app instead (v1 parity: logged out that shows login, logged in the app).
    // Always the session-neutral root: defaultBackTo is an address inside the
    // mini-app's router, on the link operator's session, not the visitor's.
    // Read at click time: subscribing via useLocation would re-render every
    // keep-alive page on each navigation.
    const atShortLinkEntry =
      shortLink !== null &&
      history.location.pathname === shortLink.entryPathname
    if (atShortLinkEntry) {
      shortLink.exitToApp('/assignments')
      return
    }
    const parentPath = props.defaultBackTo ?? '/assignments'
    if (tabNavigation) {
      tabNavigation.goBack(parentPath)
      return
    }
    if (window.history.length > 1) history.goBack()
    else history.replace(parentPath)
  }

  // Edge swipe → same back handler (see useSwipeBack for why it's manual)
  const swipeRef = useSwipeBack<HTMLIonContentElement>(handleBack)

  return (
    <>
      {props.hideHeader !== true && (
        <IonHeader translucent>
          <IonToolbar style={{ '--border-width': '0px' }}>
            <IonButtons slot='start'>
              <IonButton onClick={handleBack}>
                <IonIcon slot='start' icon={chevronBackOutline} />
                Back
              </IonButton>
            </IonButtons>
            <IonTitle>{props.title}</IonTitle>
            {props.headerEndButtons && (
              <IonButtons slot='end'>{props.headerEndButtons}</IonButtons>
            )}
          </IonToolbar>
        </IonHeader>
      )}
      <IonContent fullscreen ref={swipeRef}>
        {props.hideHeader !== true && props.collapsingTitle && (
          <IonHeader collapse='condense'>
            <IonToolbar>
              {props.title && <IonTitle size='large'>{props.title}</IonTitle>}
            </IonToolbar>
          </IonHeader>
        )}

        {props.children}
      </IonContent>
    </>
  )
}
